<!-- Institute of Design and Innovation (email idi@unm.edu). -->

<!-- Appointment Types listing webpage -->
<template>
  <div class="appointment-types page">
    <div class="page-heading">
      <h1>Appointment Types</h1>
      <av-button @click="showAppointmentForm"><i class="fal fa-plus-circle"></i> New</av-button>
      <av-button @click="showRestoreAppointmentForm"><i class="fal fa-trash-restore"></i> Restore</av-button>
    </div>

    <div class="page-content">
      <!-- List of AppointmentTypes -->
      <ul class="page-listing appointment-type-listings">
        <div v-for="appointmentType of appointmentTypesActive" :key="appointmentType.id" class="appointment-type-listing">
          <span class="color"><div class="color-dot" :style="{ background: appointmentType.color + '33', 'border-color': appointmentType.color }"></div></span>
          <span class="name">{{ appointmentType.name }}</span>
          <span class="duration"><i class="fal fa-clock"></i> {{ formatMinutes(appointmentType.duration) }}</span>
          <span class="type">
            <i v-if="appointmentType.isTask" class="fal fa-tasks"></i>
            <i v-if="!appointmentType.isTask" class="fal fa-calendar"></i>
            {{ appointmentType.isTask ? 'Task' : 'Appointment' }}
          </span>
          <span class="status">
            <i v-if="appointmentType.public" class="fal fa-eye"></i>
            <i v-else class="fal fa-eye-slash"></i>
            {{ appointmentType.public ? 'Public' : 'Private' }}
          </span>
          <span class="actions">
            <router-link :to="'/appointment-types/' + appointmentType.id"><a class="shaded black"><i class="fal fa-edit"></i> Edit</a></router-link>
            <a class="shaded red" @click="removeType(appointmentType.id)"><i class="fal fa-trash"></i> Remove</a>
          </span>
        </div>
      </ul>
    </div>

    <!-- AppointmentType Form -->
    <appointment-type-form
      :visible="shouldShowAppointmentForm"
      @updated="handleAppointmentTypeCreated"
      @close="shouldShowAppointmentForm = false"
    ></appointment-type-form>

    <!-- Restore AppointmentType -->
    <av-modal :visible="shouldRestoreType" @close="shouldRestoreType = false">
      <div class="modal-header">Restoring Appointment Type</div>
      <div class="modal-content">
        <div class="av-form-field">
          <label class="av-label">Removed Appointment Type(s)</label>
          <av-select
            :items="appointmentTypesRemoved"
            displayKey="name"
            valueKey="id"
            placeholder="Select Appointment Type to Restore"
            v-model="selected"
          ></av-select>
        </div>
        <div class="modal-actions">
          <av-button class="hollow error" @click="shouldRestoreType = false">Cancel</av-button>
          <av-button @click="this.restoreAppointmentType(selected)">Restore</av-button>
        </div>
    </div>
    </av-modal>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import appointmentTypeService from '../common/appointmentTypeService'
import TimeService from '../common/timeService'
import { AppointmentType } from '../models/appointmentType'
import AppointmentTypeForm from '../components/AppointmentTypeForm.vue'

export default defineComponent({
  name: 'AppointmentTypesList',

  data () {
    return {
      /** AppointmentType Form Info */
      appointmentForm: null as unknown as AppointmentType,

      /** List of AppoointmentTypes */
      appointmentTypes: [] as Array<AppointmentType>,

      /** Indicator to show the appointment form */
      shouldShowAppointmentForm: false,

      shouldRestoreType: false
    }
  },

  mounted () {
    // Fetch appointment types
    appointmentTypeService.getAll().then(res => {
      this.appointmentTypes = res.data
    })
  },

  computed: {
    // returns list of appointment types removed
    appointmentTypesRemoved: function (): Array<AppointmentType> {
      return this.appointmentTypes.filter(type => type.removed)
    },

    // returns list of appointment types still active
    appointmentTypesActive: function (): Array<AppointmentType> {
      return this.appointmentTypes.filter(type => !type.removed)
    }
  },

  methods: {
    /**
     * Creates a new AppoiontmentType
     */
    handleAppointmentTypeCreated (appointmentType: AppointmentType) {
      this.appointmentTypes.push(appointmentType)
      this.shouldShowAppointmentForm = false
      appointmentType.removed = false
      this.$router.push(`/appointment-types/${appointmentType.id}`)
    },

    /**
     * Returns the given duration of time in hours
     * @params minutes: Number of minutes
     */
    formatMinutes (minutes: number): string {
      return TimeService.minutesToHoursFormatted(minutes)
    },

    /**
     * Resets the new AppointmentType form data and shows the form
     */
    showAppointmentForm () {
      this.appointmentForm = {
        id: 0,
        name: '',
        description: '',
        duration: 60,
        public: true,
        isTask: false,
        removed: false,
        color: '#CCCCCC'
      }

      this.shouldShowAppointmentForm = true
    },

    /**
     * Show dropdown list of removed appointments
     */
    showRestoreAppointmentForm () {
      this.shouldRestoreType = true
    },

    /**
     * Restore appointment type that was removed
     */
    restoreAppointmentType (id: number) {
      if (window.confirm('Are you sure you want to Restore?')) {
        appointmentTypeService.restoreType(id).then(res => {
          this.appointmentTypes.push(res.data)
        })
      }
      this.shouldRestoreType = false
    },

    /**
     * Removes the given appointment type
     * @param: The offering
     */
    removeType (id: number) {
      if (window.confirm('Are you sure you want to Remove?')) {
        appointmentTypeService.removeType(id).then(res => {
          const ind = this.appointmentTypes.findIndex(at => at.id === id)
          if (ind !== -1) {
            this.appointmentTypes.splice(ind, 1)
          }
        })
      }
    }
  },

  components: {
    AppointmentTypeForm
  }
})
</script>
